/**
*****************************************
*										                    *
* NAGARRO  - CSX - CONFIDENTIAL         *
*										                    *
*****************************************
*/

import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { ConfigService } from '../../../core/services/config.service';
import { EditModeService } from '../../../core/services/edit-mode.service';
import { LmsLoaderService } from '../../../core/services/lms-loader.service';
import { LmsRendererService } from '../../../core/services/lms-renderer.service';
import { LocoDetail } from '../../../core/model/loco-detail.model';
import { LocomotiveErrors } from '../../../core/model/locomotive-error.model';
import { Tab } from '../../../core/model/tab.model';
import { TrainData } from '../../../core/model/train-data.model';
import { TrainEditOps } from '../../shared/model/train-edit-operation.model';
import { debounceTime } from 'rxjs/operators';

/**
 * This train data container component created for
 * train related information and display locomotive associated with that.
 * @export
 * @class LmsTrainDataContainerComponent
 * @implements {OnDestroy}
 */
@Component({
  selector: 'lms-train-data-container',
  templateUrl: './lms-train-data-container.component.html',
  styleUrls: ['./lms-train-data-container.component.scss']
})
export class LmsTrainDataContainerComponent implements OnDestroy, OnChanges {
  locos: String[][];

  configData: any;

  @Input() trainData: TrainData;

  @Input() selectedTab: Tab;

  @Input() trainEdit: TrainEditOps;

  @Input() topToBottom: string;

  @Input() errorLocoUpdt = false;

  @Input() locoUpdateErrors: Map<string, LocomotiveErrors>;

  @Output() onLocoSelect: EventEmitter<LocoDetail> = new EventEmitter<LocoDetail>();

  @Output() editAction: EventEmitter<TrainEditOps> = new EventEmitter<TrainEditOps>();

  @Output() actionTrigger: EventEmitter<string> = new EventEmitter<string>();

  @Output() reSeqTrainData: EventEmitter<TrainData> = new EventEmitter<TrainData>();

  @Output() editTrainData: EventEmitter<LocoDetail[]> = new EventEmitter<LocoDetail[]>();

  @Output() updateTab: EventEmitter<Tab> = new EventEmitter<Tab>();

  trainEditForm: UntypedFormGroup;

  showLocmotiveGrid = true;

  trainLabel: any;

  locoLabel: any;

  draggedLoco: LocoDetail;

  dragFrom: number;

  dropAt: number;

  draggedElem: any;

  imagesPath: any;

  resequencePop = false;

  formSubmitted: boolean;

  tabIndex: number;

  trainDataHeight: number;

  upperHeight = 125;


  /**
   * Creates an instance of LmsTrainDataContainerComponent and inject the ConfigService.
   * @param {ConfigService} configService
   * @memberOf LmsTrainDataContainerComponent
   */
  constructor(private configService: ConfigService, private _fb: UntypedFormBuilder,
    private editModeService: EditModeService, private lmsLoaderService: LmsLoaderService,
    private lmsRendererService: LmsRendererService) {
    this.configData = this.configService.getConfigData();
    this.trainLabel = this.configData.trainLabel;
    this.locoLabel = this.configData.locoLabel;
    this.imagesPath = this.configData.images;
    this.tabIndex = this.configData.tabIndex.detailPage;
    this.formSubmitted = false;
    this.editModeService.setEditResetMode(false);
    this.prepareHeightForPage();
    this.lmsRendererService.resizeObervable.pipe(debounceTime(10)).subscribe((isResize) => {
      if (isResize) {
        this.prepareHeightForPage();
      }
    });
  }

  prepareHeightForPage() {
    const windowHeight = this.lmsRendererService.getWindowHeight();
    this.trainDataHeight = windowHeight - this.upperHeight - 20;
  }

  /**
   * @memberOf LmsTrainDataContainerComponent
   */
  ngOnChanges() {

    if (this.trainData && this.trainData.locoDetails && this.trainData.locoDetails.length > 0) {
      this.showLocmotiveGrid = true;
    } else {
      this.showLocmotiveGrid = false;
    }
    if (!this.trainEdit.submitAction && this.trainEdit.locoEditMode) {
      this.trainEditForm = this._fb.group({
        editableLocoList: this._fb.array([])
      });

      const control = <UntypedFormArray>this.trainEditForm.controls['editableLocoList'];
      for (let i = 0; i < this.trainEdit.selectedLocomotives.length; i++) {
        control.push(this.initeditableLocomotives(this.trainEdit.selectedLocomotives[i]));
      }
      this.formSubmitted = false;
    }
    setTimeout(() => {
      const parentElement = document.getElementById('trainEditModeWidth');
      if (parentElement && this.trainData && this.trainData.locoDetails) {
        const widthApplied = this.trainData.locoDetails.length * 220 + 'px';
        parentElement.setAttribute('style', `width: ${widthApplied}`);
      }
    }, 4);

    if (this.selectedTab.mode && this.selectedTab.mode === 'edit' && this.trainData) {
      setTimeout(() => {
        this.trainEdit = {
          selectedLocomotives: this.trainData.locoDetails,
          draggable: false,
          submitAction: false,
          locoSelection: false,
          locoEditMode: true,
          dragMode: false,
          editDisable: true,
          cancelDisable: false,
          submitDisable: false,
          otherActionDisable: true
        };
        this.selectedTab.mode = 'view';
        this.updateTab.emit(this.selectedTab);
        this.editAction.emit(this.trainEdit);
      }, 1);
    }
    this.checkEditModeStatus();
  }

  /**
   * this method is used to check edit mode status
   * of screen user currently on
   * @private
   * @memberOf LmsTrainDataContainerComponent
   */
  private checkEditModeStatus() {
    if (this.trainData && this.selectedTab.subTypeDetail[this.selectedTab.subType.toLowerCase()].status !== 'blank'
      && this.trainEdit && (this.trainEdit.dragMode || this.trainEdit.locoEditMode)) {
      const editModeStatus = {
        status: true,
        message: 'Unsaved data will be lost. Do you want to continue?'
      };
      setTimeout(() => {
        this.editModeService.setEditMode(editModeStatus);
      }, 10);
    } else {
      const editModeStatus = {
        status: false,
        message: ''
      };
      this.editModeService.setEditMode(editModeStatus);
    }

  }

  /**
   * This mehtod is used to initialize editable
   * locomotive fields
   * @private
   * @param {LocoDetail} loco
   * @returns
   * @memberOf LmsTrainDataContainerComponent
   */
  private initeditableLocomotives(loco: LocoDetail) {
    return this._fb.group({
      locoID: [loco.locomotiveIdentifier],
      sequence: [loco.consistSequence, Validators.required],
      fuelLocation: [loco.lastFueledLocation],
      fuelDate: [loco.lastFueledDate && loco.lastFueledDate.length === 10 ? loco.lastFueledDate : ''],
      minDate: [this.getFormattedMinDateForEdit(loco.lastFueledDate)],
      miles: [loco.miles],
      remarks: [loco.remarks],
      deadIso: [loco.deadIsolatedFlag ? loco.deadIsolatedFlag : ' '],
      direction: [loco.directionFacing ? loco.directionFacing : ' '],
      fuelDateYear: ''
    });
  }

  /**
   * this mehtod is used to convert string value of fuel date
   * to Date type value so that we can show user a calendar
   * in edit mode
   * @private
   * @param {string} date
   * @returns {Date}
   * @memberOf LmsTrainDataContainerComponent
   */
  private getFormattedFuelDateForEdit(date: string): Date {
    const newDate: Date = new Date();
    if (date && date.length === 8) {
      const month = date.substring(0, 2);
      const day = date.substring(2, 4);
      newDate.setMonth(Number.parseInt(month) - 1);
      newDate.setDate(Number.parseInt(day));
      if (newDate > new Date()) {
        newDate.setFullYear(newDate.getFullYear() - 1);
      }
    }
    return newDate;
  }


  /**
   * this mehtod is used to convert string value of Min date
   * to Date type value so that we can show user a calendar
   * in edit mode
   * @private
   * @param {string} date
   * @returns {Date}
   * @memberOf LmsTrainDataContainerComponent
   */
  private getFormattedMinDateForEdit(date: string): Date {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() - 1);
    return newDate;
  }

  /**
   * This method is used to handle the submit
   * operation when user edit the locomotives
   * @param {FormGroup} form
   * @memberOf LmsTrainDataContainerComponent
   */
  public saveEditableTrainData(form: UntypedFormGroup, event) {
    if (event) {
      event.preventDefault();
    }
    this.formSubmitted = true;
    if (form.valid) {
      this.lmsLoaderService.showLoader();
      const values = form.value.editableLocoList;
      this.trainEdit.selectedLocomotives = this.getFormValues(values, this.trainEdit.selectedLocomotives);
      this.editTrainData.emit(this.trainEdit.selectedLocomotives);
    }
  }

  /**
   * this method is used to extract editable values
   * and update these values to main trainData object
   * @private
   * @memberOf LmsTrainDataContainerComponent
   */
  private assignUpdatedValuesToTrain() {
    for (let i = 0; i < this.trainData.locoDetails.length; i++) {
      for (let j = 0; j < this.trainEdit.selectedLocomotives.length; j++) {
        if (this.trainData.locoDetails[i].locomotiveIdentifier === this.trainEdit.selectedLocomotives[j].locomotiveIdentifier) {
          this.trainData.locoDetails[i] = this.trainEdit.selectedLocomotives[j];
        }
      }
    }
  }

  /**
   * this mehtod is helper method for
   * assignUpdatedValuesToTrain()
   * @private
   * @param {*} values
   * @param {LocoDetail[]} locomotives
   * @returns {LocoDetail[]}
   * @memberOf LmsTrainDataContainerComponent
   */
  private getFormValues(values: any, locomotives: LocoDetail[]): LocoDetail[] {

    for (let i = 0; i < values.length; i++) {
      locomotives[i].deadIsolatedFlag = values[i].deadIso;
      locomotives[i].directionFacing = values[i].direction;
      locomotives[i].lastFueledDate = this.getFuelDateValuesFromForm(values[i].fuelDate);
      locomotives[i].lastFueledLocation = values[i].fuelLocation;
      if (locomotives[i].lastFueledLocation) {
        locomotives[i].lastFueledLocation = locomotives[i].lastFueledLocation.toUpperCase();
      }
      locomotives[i].miles = values[i].miles;
      locomotives[i].remarks = values[i].remarks;
      if (locomotives[i].remarks) {
        locomotives[i].remarks = locomotives[i].remarks.toUpperCase();
      }
    }
    return locomotives;
  }

  /**
   * this method is used to convert duel date from
   * from date obj to string obj to update on server
   * @private
   * @param {Date} date
   * @returns {string}
   * @memberOf LmsTrainDataContainerComponent
   */
  private getFuelDateValuesFromForm(dateString: string): string {
    let date;
    if (dateString && dateString.length > 7) {
      date = new Date(dateString);
    } else {
      return '';
    }
    let returnString = '';
    let month = '';
    let day = '';
    if (date) {
      month = (date.getMonth() + 1).toString();
      if (month && month.length === 1) {
        month = '0' + month;
      }
      day = date.getDate().toString();
      if (day && day.length === 1) {
        day = '0' + day;
      }
      returnString = month + day;
    }
    return returnString;
  }

  /**
   * this method is used to redirect event
   * @param {any} event
   * @memberOf LmsTrainDataContainerComponent
   */
  public editActionTrigger(event) {
    if (event) {
      this.editAction.emit(event);
    }
  }

  /**
   * this method is used to redirect event
   * @param {any} event
   * @memberOf LmsTrainDataContainerComponent
   */
  public actionTriggered(event) {
    if (event) {
      this.actionTrigger.emit(event);
    }
  }

  /**
   * This method invoked when triggered previous and next icon in LocoDetail datatable.
   * @param {boolean} isPreviousDirection
   * @memberOf LmsTrainDataContainerComponent
   */
  scrollLocos(isPreviousDirection: boolean) {
    if (this.trainData && this.trainData.locoDetails) {
      if (isPreviousDirection) {
        if ('arrow-enable' === this.displayLeftArrow()) {
          this.scrollEffect(-1);
        }
      } else {
        if ('arrow-enable' === this.displayRightArrow()) {
          this.scrollEffect(1);
        }
      }
    }
  }

  /**
   * this method is used to apply effect to scrollBar
   * @param {number} direction
   * @memberOf LmsTrainDataContainerComponent
   */
  scrollEffect(direction: number) {
    let count = 0;
    const element = document.getElementById('loco-data-scroll');
    const interval = setInterval(function () {
      count++;
      element.scrollLeft = element.scrollLeft + (direction * 20);
      if (count === 11) {
        clearInterval(interval);
      }
    }, 50);
  }

  /**
   * This is used to enable or disable the Right slider event
   * @returns {string}
   * @memberOf LmsTrainDataContainerComponent
   */
  displayRightArrow(): string {
    const element = document.getElementById('loco-data-scroll');
    if ((element.scrollWidth - element.clientWidth) === element.scrollLeft) {
      return 'arrow-disable';
    } else {
      return 'arrow-enable';
    }
  }

  /**
   * This is used to enable or disable the Left slider event
   * @returns {string}
   * @memberOf LmsTrainDataContainerComponent
   */
  displayLeftArrow(): string {
    const element = document.getElementById('loco-data-scroll');
    if (0 === element.scrollLeft) {
      return 'arrow-disable';
    } else {
      return 'arrow-enable';
    }
  }

  /**
   * This method involed when datatable LocoDetail column is drop to other place.
   * @param {any} event
   * @param {LocoDetail} loco
   * @memberOf LmsTrainDataContainerComponent
   */
  public drop(event, loco: LocoDetail) {
    if (this.draggedLoco.locomotiveIdentifier !== loco.locomotiveIdentifier) {
      this.dropAt = this.getIndexPosition(loco);
      this.dragFrom = this.getIndexPosition(this.draggedLoco);
      this.checkForSelectionClass();
      this.draggedLoco.isGrayed = true;
      this.moveLocomotive(this.dropAt, this.dragFrom);
      this.trainEdit.dragMode = true;
      this.trainEdit.editDisable = true;
      this.trainEdit.otherActionDisable = true;
      this.trainEdit.locoSelection = false;
      this.trainEdit.cancelDisable = false;
      this.trainEdit.submitDisable = false;
      this.editAction.emit(this.trainEdit);
      this.reSeqTrainData.emit(this.trainData);
    }
  }

  checkForSelectionClass() {
    const element = document.getElementById('selection' + this.draggedLoco.locomotiveIdentifier);
    let svgElem;
    if (element) {
      svgElem = element.querySelector('.train-page-svg');
      if (svgElem && svgElem.classList.contains('selectedDraged')) {
        svgElem.classList.remove('selectedDraged');
      }
    }
    if (this.trainEdit && this.trainEdit.selectedLocomotives && this.trainEdit.selectedLocomotives.length > 0) {
      this.trainEdit.selectedLocomotives.forEach((locomotive) => {
        const selectedElem = document.getElementById('selection' + locomotive.locomotiveIdentifier);
        let selectedSvgElem;
        if (selectedElem) {
          if (selectedElem && selectedElem.classList.contains('locomotiveSelection')) {
            selectedElem.classList.remove('locomotiveSelection');
          }
          selectedSvgElem = selectedElem.querySelector('.train-page-svg');
          if (selectedSvgElem && selectedSvgElem.classList.contains('selectedDraged')) {
            selectedSvgElem.classList.remove('selectedDraged');
          }
        }
      });
      this.trainEdit.selectedLocomotives = [];
    }
  }

  /**
   * this method is used to get index position
   * of given locomotive from the list of
   * locomotives associated with this train
   * @private
   * @param {LocoDetail} locomotive
   * @returns {number}
   * @memberOf LmsTrainDataContainerComponent
   */
  private getIndexPosition(locomotive: LocoDetail): number {
    let position: number;
    position = this.trainData.locoDetails.indexOf(locomotive);
    return position;
  }

  /**
   * This method invoked when datatable LocoDetail column is move through draggable.
   * @param {number} dropAt
   * @param {LocoDetail} dragFrom
   * @memberOf LmsTrainDataContainerComponent
   */
  private moveLocomotive(dropAt: number, dragFrom: number) {
    const locos: Array<LocoDetail> = this.trainData.locoDetails;
    if (dragFrom < dropAt) {
      let i: number;
      for (i = dragFrom; i < dropAt; i++) {
        locos[i] = locos[i + 1];
      }
      locos[i] = this.draggedLoco;
      this.trainData.locoDetails = locos;
    }
    if (dragFrom > dropAt) {
      let i: number;
      for (i = dragFrom; i > dropAt; i--) {
        locos[i] = locos[i - 1];
      }
      if (i < 0) {
        i++;
      }
      locos[i] = this.draggedLoco;
      this.trainData.locoDetails = locos;
    }
  }

  /**
   * This method invoked when datatable LocoDetail column is start drag.
   * @param {any} event
   * @param {LocoDetail} loco
   * @memberOf LmsTrainDataContainerComponent
   */
  public dragStart(event, loco: LocoDetail) {
    this.draggedLoco = loco;
    event.dataTransfer.effectAllowed = 'move';
    const dragContainer = event.target.children[0].children[0];
    dragContainer.classList.add('locomotiveSelection');
    dragContainer.querySelectorAll('.train-page-svg')[0].classList.add('selectedDraged');
    event.target.classList.add('dragSelect');
    event.target.classList.add('over');
    this.draggedElem = event.target;
    this.actionTrigger.emit('hideContextMenu');

  }

  /**
   * This method invoked after datatable LocoDetail column is dropped.
   * @param {any} event
   * @memberOf LmsTrainDataContainerComponent
   */
  public dragEnd(event) {
    const dragContainer = event.target.children[0].children[0];
    dragContainer.classList.remove('locomotiveSelection');
    dragContainer.querySelectorAll('.train-page-svg')[0].classList.remove('selectedDraged');
    this.draggedElem.classList.remove('dragSelect');
    this.draggedElem.classList.remove('over');
    this.draggedElem = null;
  }

  /**
   * This method invoked when datatable LocoDetail column is enter in droppable zone.
   * @param {any} event
   * @returns
   * @memberOf LmsTrainDataContainerComponent
   */
  public dragEnter(event) {
    event.preventDefault();
    return true;
  }

  /**
   * This method invoked when datatable LocoDetail column is drag over the droppable zone.
   * @param {any} event
   * @returns
   * @memberOf LmsTrainDataContainerComponent
   */
  public dragOver(event) {
    return false;
  }

  /**
   * This method invoked when right click on locomotive icon
   * for displaying locomotive information in context menu.
   * @param {any} event
   * @param {LocoDetail} loco
   * @returns
   * @memberOf LmsTrainDataContainerComponent
   */
  public showLocoContextMenu(loco: LocoDetail) {
    this.onLocoSelect.emit(loco);
  }

  /**
   * @memberOf LmsTrainDataContainerComponent
   */
  ngOnDestroy() {
  }
}
